import React, { useState, useRef } from 'react'
import { Range } from 'react-date-range'
import { AuditRecord } from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { useIsShowModal, useHideModal } from 'src/Modal/ModalStateContext'
import { ModalForm, ModalFormId } from 'src/components/ModalForms'
import { FieldLabel } from 'src/components/ModalForms/ModalForm.styles'
import { useCurrentAudit } from 'src/components/async/model/audit'
import { PrimaryButton } from 'src/components/Reusable/Buttons'
import log from 'loglevel'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { FlexAlign, FlexRow } from '@trustero/app/src/components/Reusable/Flex'
import { ControlTestCost } from 'src/pages/AuditBot/AuditBot.components'
import { useListControls } from '@trustero/app/src/pages/Controls/ControlsIndexPage/AddControl/BringIntoAudit'
import { useAuditBotModal } from 'src/components/ModalForms/Reusable/SelectControls/AuditScanModal.hooks'
import { DateLimitInfoSection } from 'src/pages/AccountSettings/AccountSettings.components'
import { TrusteroDateRange } from 'src/components/Reusable/TrusteroDateRange'
import { useAudits } from 'src/components/async/model/audit/useAudit'
import { getDaysBetweenWithDate } from 'src/Utils/formatDate'
import { useDateLimit } from 'src/pages/AccountSettings/AccountSettings.hooks'
import { useIsInternalUser } from 'src/context/authContext'
import { usePromptOverrideSources } from 'src/pages/PromptOverrides/PromptOverridesPage.hooks'
import { PromptOverrideSourcePicker } from 'src/pages/PromptOverrides/modals/PromptOverrideSourceModal.components'
import { AuditsDropdown } from '../AuditsDropdown/AuditsDropdown'
import { useStartAuditRun } from '../AuditBot.hooks'
import { AuditBotCost } from '../AuditBot.constants'
import {
  AuditBotCheckBox,
  DateLimitInfoContainer,
} from './AuditBotModal.styles'
import { LineText } from './AuditBotModal.components'

export const AuditBotModal = ({
  controlId,
}: {
  controlId?: string
}): JSX.Element => {
  const [navigateOnSubmit, setNavigateOnSubmit] = useState<boolean>(false)
  const [isBusy, setIsBusy] = useState<boolean>(false)
  const [overrideSourceId, setOverrideSourceId] = useState<string>('')
  const [showOverridePicker, setShowOverridePicker] = useState<boolean>(false)
  const isInternalUser = useIsInternalUser()
  const {
    data: promptOverrideSourcesData,
    isLoading: promptOverrideSourcesLoading,
    error: promptOverrideSourcesError,
  } = usePromptOverrideSources()
  const openAuditBotModal = useAuditBotModal()
  const {
    data: audits,
    isLoading: auditsLoading,
    error: auditsError,
  } = useAudits()
  const startAuditRun = useStartAuditRun(navigateOnSubmit)
  const {
    data: dateLimitInfo,
    isLoading: isAccountLoading,
    error: dateLimitError,
  } = useDateLimit()
  const show = useIsShowModal(ModalFormId.RUN_AUDITBOT)
  const [audit, setAudit] = useState<AuditRecord | undefined>()
  const now = new Date()
  const [datePickerRange, setDatePickerRange] = useState<Range>({
    endDate: new Date(new Date().setDate(now.getDate() - 90)),
  })
  const [isChecked, setIsChecked] = useState<boolean>(true)
  const [error, setError] = useState<boolean>(false)
  const isAuditRunning = useRef<boolean>(false)
  const {
    data: currentAudit,
    isLoading: isCurrentAuditLoading,
    error: currentAuditError,
  } = useCurrentAudit()
  const {
    data,
    isLoading,
    error: controlsError,
  } = useListControls({
    excludeFromAudit: false,
    isDismissed: false,
    complianceFrameworkIds: [],
    auditId: audit?.getId() || currentAudit?.getId() || '',
  })

  const onClose = () => {
    setAudit(undefined)
    setError(false)
    setIsChecked(true)
  }
  const onHide = useHideModal({
    onHide: onClose,
    modalId: ModalFormId.RUN_AUDITBOT,
  })

  const hideControlsModal = useHideModal({
    onHide: onClose,
    modalId: ModalFormId.AUDIT_SCAN_MODAL,
  })

  if (
    isCurrentAuditLoading ||
    isLoading ||
    isAccountLoading ||
    auditsLoading ||
    promptOverrideSourcesLoading
  ) {
    return <></>
  }

  const dateLimit = dateLimitInfo?.getDateLimit()

  if (!data || !dateLimit) {
    if (controlsError) {
      log.error('Error fetching auditbot controls in auditbot modal', error)
    } else if (auditsError) {
      log.error('Error fetching audits in auditbot modal', auditsError)
    } else if (dateLimitError) {
      log.error('Error fetching date limit in auditbot modal', dateLimitError)
    } else if (currentAuditError) {
      log.error(
        'Error fetching current audit in auditbot modal',
        currentAuditError,
      )
    } else if (promptOverrideSourcesError) {
      log.error(
        'Error fetching prompt override sources in auditbot modal',
        promptOverrideSourcesError,
      )
    }

    // TODO: Work with design to handle more gracefully
    return <></>
  }

  const control = data
    .getItemsList()
    .find((control) => control.getId() === controlId)

  const auditToScan = currentAudit ? currentAudit.getId() : audit?.getId()
  const selectedAudit =
    audit?.getName() || 'Select an audit to examine and test'
  const controls = data.getItemsList()
  const validAudits = audits?.getItemsList().filter((audit) =>
    // if we're scanning a control, make sure the audits dropdown only shows audits that the control is in
    control
      ? control.getAuditIdsList().includes(audit.getId()) &&
        !audit.getIsClosed()
      : !audit.getIsClosed(),
  )
  // Ensure we hide the back button if running from within an audit

  const onSelectAudit =
    (audit?: AuditRecord) => (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault()
      setAudit(audit)
    }

  const triggerAuditRun = async (
    selectedControls: string[] = controlId ? [controlId] : [],
  ) => {
    if (isAuditRunning.current) return

    log.debug(
      `Starting audit run for auditId: ${auditToScan} and dateLimit: ${dateLimit}`,
    )
    try {
      // Ensure user cannot accidentally trigger multiple scans
      isAuditRunning.current = true
      setIsBusy(true)
      hideControlsModal()
      await startAuditRun(
        auditToScan,
        auditToScan
          ? undefined
          : {
              startDate: dateLimit?.toDate() || new Date(),
              endDate: now,
            },
        selectedControls,
        undefined,
        undefined,
        overrideSourceId || undefined,
      )
      onHide()
    } catch (err) {
      log.error(
        `Error with AI Examination and Test for auditId: ${auditToScan} and dateLimit: ${dateLimit}`,
        err,
      )
      showInfoToast(
        'There was an error examining and testing your controls. Please try again.',
      )
    } finally {
      isAuditRunning.current = false
      setIsBusy(false)
    }
  }

  const nextStep = () => {
    // If the user includes all controls, trigger the job right away
    // We will return w/ an API error if the user has no controls in the Audit
    // If the user is accessing the modal from the test tab of a control
    // show page and we have a control id, we will trigger the job right away
    isChecked || controlId
      ? triggerAuditRun()
      : openAuditBotModal({
          onBackModalId: ModalFormId.RUN_AUDITBOT,
          auditId: audit ? audit.getId() : undefined,
        })
  }

  const showCheckbox = !controlId
  const submitBtn = (
    <PrimaryButton onClick={nextStep} text={isChecked ? 'Run' : 'Next'} />
  )

  const showControlTestCount = controlId || (showCheckbox && isChecked)

  const daysBetween = getDaysBetweenWithDate(
    now,
    datePickerRange.endDate || now,
  )

  return (
    <>
      <ModalForm
        show={show}
        hide={onHide}
        formId={ModalFormId.RUN_AUDITBOT}
        size="xl"
        customSubmitButton={isBusy ? undefined : submitBtn}
        busy={isBusy}
        busyText={isBusy ? 'Indexing documents' : undefined}
        showOverflow
        title="Examine and Test"
      >
        <form id={ModalFormId.RUN_AUDITBOT}>
          {!!validAudits?.length && !currentAudit && (
            <FieldLabel
              validateForm={error}
              isInvalid={!audit}
              message="Please select an audit to examine and test"
            >
              <AuditsDropdown
                audits={validAudits}
                selectedItem={selectedAudit}
                onAuditSelected={onSelectAudit}
                allowClear
              />
            </FieldLabel>
          )}
          {!auditToScan && (
            <>
              {!!validAudits?.length && !currentAudit && <LineText text="OR" />}
              <>
                {dateLimit ? (
                  <>
                    <p>Run in continuous compliance.</p>
                    <DateLimitInfoContainer>
                      <DateLimitInfoSection
                        dateLimit={dateLimit}
                        onClickHandler={() => onHide()}
                      />
                    </DateLimitInfoContainer>
                  </>
                ) : (
                  <>
                    <p>Choose your start date</p>
                    <TrusteroDateRange
                      range={datePickerRange}
                      rangeSet={setDatePickerRange}
                      single
                    />
                    <FieldLabel>
                      Only consider evidence posted in the last {daysBetween}{' '}
                      days
                    </FieldLabel>
                  </>
                )}
              </>
            </>
          )}
          {showCheckbox && (
            <AuditBotCheckBox
              label={`Include all controls${audit ? ' for this audit' : ''}`}
              checked={isChecked}
              onChange={() => setIsChecked(!isChecked)}
            />
          )}
          {controlId && (
            <AuditBotCheckBox
              label="Navigate to detailed AI Examination and Test result after running."
              checked={navigateOnSubmit}
              onChange={() => setNavigateOnSubmit((p) => !p)}
            />
          )}
          {showControlTestCount && (
            <FlexRow justify={FlexAlign.FLEX_END} style={{ marginTop: '8px' }}>
              <ControlTestCost
                costPerControl={AuditBotCost.ControlTest}
                controlQuantity={controlId ? 1 : controls.length}
                style={{ cursor: isInternalUser ? 'pointer' : undefined }}
                onClick={
                  isInternalUser
                    ? () => setShowOverridePicker((p) => !p)
                    : undefined
                }
              />
            </FlexRow>
          )}
          {showOverridePicker && isInternalUser && (
            <PromptOverrideSourcePicker
              selectedSourceId={overrideSourceId}
              onSelect={(sourceId: string) => setOverrideSourceId(sourceId)}
              promptOverrideSources={
                promptOverrideSourcesData?.getSourcesList() || []
              }
              isLoading={promptOverrideSourcesLoading}
            />
          )}
        </form>
      </ModalForm>
    </>
  )
}
